import { useEffect, useRef } from 'react'
import { useSpring, useTransform, motion, SpringOptions } from 'framer-motion'
import { cn } from '../../lib/utils'

interface AnimatedNumberProps {
  value: number
  prefix?: string
  suffix?: string
  decimals?: number
  duration?: number
  className?: string
  compact?: boolean
}

const springConfig: SpringOptions = { stiffness: 80, damping: 20 }

function formatCompact(n: number, decimals: number): string {
  const abs = Math.abs(n)
  if (abs >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (abs >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return n.toFixed(decimals)
}

export function AnimatedNumber({
  value,
  prefix = '$',
  suffix = '',
  decimals = 0,
  className,
  compact = true,
}: AnimatedNumberProps) {
  const spring = useSpring(0, springConfig)
  const display = useTransform(spring, (v) => {
    if (compact) return `${prefix}${formatCompact(v, decimals)}${suffix}`
    return `${prefix}${v.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })}${suffix}`
  })

  useEffect(() => {
    spring.set(value)
  }, [value, spring])

  return (
    <motion.span className={cn('font-mono tabular-nums', className)}>
      {display}
    </motion.span>
  )
}
